import {
  JORNAYA_TOKEN_FORM_FIELD_NAME,
  LS_SUBMISSION_TIMEOUT,
  TRUSTED_FORM_FIELD_NAME,
} from "@lgp/shared/constants";
import AddressInput from "@lgp/shared/react/components/formik/AddressInput";
import CheckboxButtonGroupInput from "@lgp/shared/react/components/formik/CheckboxButtonGroupInput";
import FormInput from "@lgp/shared/react/components/formik/FormInput";
import FormInputMask from "@lgp/shared/react/components/formik/FormInputMask";
import JornayaInput from "@lgp/shared/react/components/formik/JornayaInput";
import RadioButtonGroupInput from "@lgp/shared/react/components/formik/RadioButtonGroupInput";
import SingleCheckboxInput from "@lgp/shared/react/components/formik/SingleCheckboxInput";
import useTcpaMemo from "@lgp/shared/react/hooks/useTcpaMemo";
import useTcpaOnClickEventListener from "@lgp/shared/react/hooks/useTcpaOnClickEventListener";
import { isEmpty } from "@lgp/shared/utils";
import { lgpClient } from "@lgp/shared/utils/LGPApiClient";
import { detectInitialPageInteraction } from "@lgp/shared/utils/browser";
import { substitutions } from "@lgp/shared/utils/string";
import { buildMultiStepLeadFormSchema } from "@lgp/shared/utils/yup";
import classNames from "classnames";
import { FastField, Form, Formik } from "formik";
import { parseFullName } from "parse-full-name";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  FormGroup,
  Row,
  Spinner,
} from "reactstrap";
import { appContext } from "./components/AppProvider";
import ProgressBar from "./components/ProgressBar";
import SearchSection from "./components/SearchSection";

const { NODE_ENV } = process.env;

let trustedFormLoaded = false;

const FORM_ID = "mslf-form";

function wait(ms = 5000) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function postNewLead(url, data) {
  const queueRes = await lgpClient.makeRequest("POST", url, {
    data,
  });
  if (queueRes.status === "processed") {
    return queueRes;
  }

  let processingRes = { status: "processing" };
  while (processingRes.status === "processing") {
    try {
      await wait();
      processingRes = await lgpClient.makeRequest(
        "GET",
        `${url}/status/${queueRes.data.queueId}`
      );
    } catch (err) {
      processingRes = { reason: err };
    }
  }

  if (processingRes.reason) {
    throw processingRes.reason;
  }

  return processingRes;
}

function App() {
  const {
    appState,
    formState,
    setFormState,
    triggerPostMessage,
    trackGAEvent,
    setPrevSection,
    setNextSection,
    setFormDisabled,
  } = useContext(appContext);

  // Gets the memoized data.
  const {
    showProgressBar,
    floatingLabels,
    formWidth,
    nextButtonLabel,
    submitButtonLabel,
    showBackButton,
    section,
    showSearchSection,
    hasPrevSection,
    hasNextSection,
    submitButtonWidth,
    primaryButtonStyles,
    maxSectionCompleted,
    totalSections,
  } = useMemo(() => {
    const showProgressBar = appState.formOptions.showProgressBar;
    const floatingLabels = appState.formOptions.floatingLabels !== false;
    const formWidth = Math.max(
      7,
      Math.min(Math.floor(appState.formOptions.formWidth / 10) + 2, 12)
    );
    const nextButtonLabel = appState.formOptions.nextButtonLabel ?? "Next";
    const submitButtonLabel =
      appState.formOptions.submitButtonLabel ?? "Get Prices Now";
    const showBackButton = appState.formOptions.showBackButton === true;
    const section = appState.sections[appState.currentSection];
    const showSearchSection = !isEmpty(section.steps);
    const hasPrevSection = appState.currentSection > 0;
    const hasNextSection =
      appState.currentSection < appState.sections.length - 1;
    const submitButtonWidth =
      hasPrevSection && showBackButton
        ? 6
        : Math.max(
            5,
            Math.min(
              Math.floor(appState.formOptions.startButtonWidth / 10) + 2,
              12
            )
          );
    let primaryButtonStyles;
    if (appState.formOptions.submitButtonColor) {
      primaryButtonStyles = {
        backgroundColor: appState.formOptions.submitButtonColor,
        borderColor: appState.formOptions.submitButtonColor,
        color: appState.formOptions.submitButtonFontColor,
      };
    }

    return {
      showProgressBar,
      floatingLabels,
      formWidth,
      nextButtonLabel,
      submitButtonLabel,
      showBackButton,
      section,
      showSearchSection,
      hasPrevSection,
      hasNextSection,
      submitButtonWidth,
      primaryButtonStyles,
      maxSectionCompleted:
        appState.maxSectionCompleted === null
          ? 0
          : appState.maxSectionCompleted + 1,
      totalSections: appState.sections.length,
    };
  }, [
    appState.formOptions,
    appState.sections,
    appState.currentSection,
    appState.maxSectionCompleted,
  ]);

  // Get the form's initial values for the current secion.
  const initialFormValues = useMemo(() => {
    const initialFormValues = {};
    section.fields.forEach((field) => {
      initialFormValues[field.name] = formState.data[field.name] || "";
    });

    return initialFormValues;
  }, [section, formState.data]);

  // Gets the form's validator.
  const validationSchema = useMemo(
    () => buildMultiStepLeadFormSchema(section.fields),
    [section]
  );

  // Gets the TCPA html.
  const tcpa = useTcpaMemo(formState.data.TCPA_Language);

  // Handle clicks on TCPA links.
  const tcpaOnClick = useTcpaOnClickEventListener(appState.parentHost);

  // Tracks form entered GA event.
  const enteredEventTrackedRef = useRef();
  const handleFormOnFocus = useCallback(
    function handleFormOnFocus() {
      if (!enteredEventTrackedRef.current) {
        enteredEventTrackedRef.current = true;
        trackGAEvent("lead_form_entered");
      }
    },
    [trackGAEvent]
  );

  // Handles click on the back button.
  const onBack = useCallback(
    function onBack() {
      setPrevSection();
    },
    [setPrevSection]
  );

  // Handles a section submission.
  const handleSectionSubmit = useCallback(
    function handleSubmit(newValues, newFormState = {}) {
      setFormState((prevState) => {
        const newData = newValues
          ? { ...prevState.data, ...newValues }
          : prevState.data;

        return {
          ...prevState,
          ...newFormState,
          data: newData,
        };
      });

      setNextSection();
    },
    [setFormState, setNextSection]
  );

  // Handles form submission.
  const onSubmit = useCallback(
    async function onSubmit(values, helpers) {
      const newValues = { ...formState.data, ...values };

      // Split name if found.
      if (isEmpty(newValues.FirstName) && !isEmpty(newValues.Name)) {
        const { first, last } = parseFullName(newValues.Name);
        newValues.FirstName = first;
        newValues.LastName = last;
      }

      // Get trusted form url from injected hidden field.
      if (isEmpty(newValues.Trusted_Form_URL)) {
        newValues.Trusted_Form_URL =
          document.getElementById(FORM_ID).elements[
            TRUSTED_FORM_FIELD_NAME
          ]?.value;
      }

      // Set the Jornaya Token Field.
      if (
        isEmpty(newValues.LeadiD_Token) &&
        !isEmpty(newValues[JORNAYA_TOKEN_FORM_FIELD_NAME])
      ) {
        newValues.LeadiD_Token = newValues[JORNAYA_TOKEN_FORM_FIELD_NAME];
      }

      // Set the Project Field.
      if (
        !isEmpty(newValues.SubProject) &&
        appState.boberdooProjectValues &&
        appState.boberdooProjectValues[newValues.SubProject]
      ) {
        newValues.Project =
          appState.boberdooProjectValues[newValues.SubProject];
      }

      // Clear dependent fields if parent fields don't have expected value.
      section.fields.forEach((field) => {
        if (
          !isEmpty(field["lf-condition-field"]) &&
          !isEmpty(newValues[field["lf-condition-field"]]) &&
          newValues[field["lf-condition-field"]] !== field["lf-condition-value"]
        ) {
          newValues[field.name] = "";
        }
      });

      if (hasNextSection) {
        handleSectionSubmit(newValues, { error: null });
      } else {
        handleSectionSubmit(null, { error: null });

        try {
          const trackingData = await triggerPostMessage(
            "lgp:get-tracking-data",
            "lgp:receive-tracking-data"
          );

          Object.assign(newValues, trackingData);

          const newLeadRes = await postNewLead(appState.endpoint, newValues);

          handleSectionSubmit(newValues, {
            isSubmitted: true,
            isConversion: newLeadRes.data.conversion,
            conversionId: newLeadRes.data.queueId,
          });
        } catch (error) {
          helpers.setSubmitting(false);
          if (error.errors) {
            let sectionHasError = false;

            section.fields.forEach(({ name: fieldName }) => {
              if (error.errors[fieldName]) {
                sectionHasError = true;
              }
            });

            if (sectionHasError) {
              helpers.setErrors(error.errors);
            } else {
              const errorKeys = Object.keys(error.errors);
              handleSectionSubmit(null, {
                error: {
                  message: NODE_ENV ? "Validation Error." : "",
                  description: error.errors[errorKeys[0]][0],
                },
              });
            }
          } else {
            handleSectionSubmit(null, {
              error: {
                message: NODE_ENV ? error.message : "",
                description:
                  appState.errorMessage ??
                  "Looks like there was an error processing your request. Please contact support if the problem persists.",
              },
            });
          }
        }
      }
    },
    [
      appState.endpoint,
      appState.errorMessage,
      appState.boberdooProjectValues,
      formState.data,
      section.fields,
      hasNextSection,
      triggerPostMessage,
      handleSectionSubmit,
    ]
  );

  // Handles loading external scripts.
  useEffect(
    function loadScriptsEffect() {
      const parentScript = document.getElementsByTagName("script")[0];

      // Load the Trusted form scripts.
      detectInitialPageInteraction().then(function () {
        // Load trusted form script.
        if (!trustedFormLoaded) {
          trustedFormLoaded = true;
          const trustedFormScript = document.createElement("script");
          trustedFormScript.type = "text/javascript";
          trustedFormScript.async = true;
          let trustedFormSrc =
            document.location.protocol === "https:" ? "https" : "http";
          trustedFormSrc += `://api.trustedform.com/trustedform.js?field=${TRUSTED_FORM_FIELD_NAME}&ping_field=xxTrustedFormPingUrl&l=${
            new Date().getTime() + Math.random()
          }`;
          if (NODE_ENV === "development" || formState.data.Test_Lead === 1) {
            trustedFormSrc += "&sandbox=true";
          }
          trustedFormScript.src = trustedFormSrc;
          parentScript.parentNode.insertBefore(trustedFormScript, parentScript);
        }
      });
    },
    [formState.data.Test_Lead, formState.data.SRC]
  );

  // Handles redirection after successful form submission.
  useEffect(
    function successRedirectEffect() {
      if (
        appState.successRedirectUrl &&
        formState.isSubmitted &&
        !formState.error
      ) {
        const redirectUrlObj = new URL(appState.successRedirectUrl);
        if (formState.conversionId) {
          redirectUrlObj.searchParams.append(
            "conversionId",
            formState.conversionId
          );
        }

        if (formState.isConversion) {
          if (formState.data.Test_Lead !== 1) {
            setFormDisabled(formState.data.Project, LS_SUBMISSION_TIMEOUT);
          }

          // Track the conversion in GA.
          trackGAEvent("lead_form_completed", {
            lead_form_section_id: "section_success",
            lead_form_transaction_id: formState.conversionId,
            currency: "USD",
            value: 1,
          });

          // Track the conversion in FB.
          if (formState.data.SRC.toLowerCase().indexOf("facebook") > -1) {
            triggerPostMessage("lgp:fb:track-event", null, {
              event: "Lead",
              parameters: {
                content_type: formState.data.SRC,
                lead_form_test_lead: formState.data.Test_Lead
                  ? `${formState.data.Test_Lead}`
                  : "0",
              },
              config: {
                eventID: `${formState.data.FBPixelID}_${formState.conversionId}`,
              },
            });
          }

          setTimeout(() => {
            triggerPostMessage("lgp:redirect", null, {
              url: redirectUrlObj.href,
            });
          }, 1000);
        } else if (formState.data.Test_Lead === 1) {
          redirectUrlObj.searchParams.append("conversion", "0");

          triggerPostMessage("lgp:redirect", null, {
            url: redirectUrlObj.href,
          });
        }
      }
    },
    [
      appState.successRedirectUrl,
      formState.isSubmitted,
      formState.isConversion,
      formState.conversionId,
      formState.data.FBBrowserID,
      formState.data.FBClickID,
      formState.data.FBPixelID,
      formState.data.FBTestEventID,
      formState.data.Email,
      formState.data.Project,
      formState.data.Landing_Page,
      formState.data.SRC,
      formState.data.Test_Lead,
      formState.data.User_Agent,
      formState.error,
      setFormDisabled,
      trackGAEvent,
      triggerPostMessage,
    ]
  );

  useEffect(() => {
    if (appState.currentSection > 0) {
      triggerPostMessage("lgp:scroll-top");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.currentSection]);

  // Display form submission success message.
  if (
    formState.isSubmitted &&
    ((!formState.isConversion && formState.data.Test_Lead !== 1) ||
      isEmpty(appState.successRedirectUrl))
  ) {
    return (
      <Container fluid className="pt-4 pb-4">
        <Row>
          <Col sm="12">
            <Alert>
              <h4 className="alert-heading">Thank you!</h4>
              <p>
                {appState.successMessage
                  ? appState.successMessage
                  : "We will expedite your request and connect you with the highest quality and most reliable specialists in you local area."}
              </p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  // Display form fields.
  return (
    <Container fluid className="pt-4 pb-4">
      <ProgressBar
        showProgressBar={showProgressBar}
        totalSections={totalSections}
        maxSectionCompleted={maxSectionCompleted}
      />
      <Row className="justify-content-center g-0">
        <Col sm={formWidth}>
          {formState.error && (
            <Alert color="danger">
              <h4 className="alert-heading">Uh oh!</h4>
              <p>
                Something went wrong.{" "}
                {formState.error.message ? formState.error.message : ""}
              </p>
              <hr />
              <p className="mb-0">{formState.error.description}</p>
            </Alert>
          )}

          <SearchSection
            show={showSearchSection}
            section={section}
            setNextSection={setNextSection}
          />

          <Formik
            initialValues={initialFormValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {(props) => {
              const { values, isValidating, isSubmitting } = props;

              const question = section["lf-question"]
                ? substitutions(section["lf-question"], values)
                : "";

              const hint = section["lf-question-hint"]
                ? substitutions(section["lf-question-hint"], values)
                : "";

              const fieldComponents = [];
              section.fields.forEach((field, i) => {
                const key = field.id ?? `${FORM_ID}_${field.name}`;
                const label = field["lf-label"]
                  ? substitutions(field["lf-label"], values)
                  : "";
                let fieldComponent;
                if (field.type === "checkbox") {
                  fieldComponent = (
                    <SingleCheckboxInput
                      key={key}
                      id={key}
                      name={field.name}
                      label={label}
                    />
                  );
                } else if (field.type === "radio buttons") {
                  fieldComponent = (
                    <RadioButtonGroupInput
                      key={key}
                      id={key}
                      name={field.name}
                      label={label}
                      options={field["lf-options"]}
                      optionLabelKey="lf-label"
                      optionValueKey="value"
                      optionIconKey="lf-icon"
                      color="secondary"
                      outline
                      stack={field.tag === "stack" || field.tag === "fieldset"}
                      group={field.tag === "group"}
                      button={field.tag === "button"}
                      btnClassName={
                        field.tag === "button" ? "with-icon" : undefined
                      }
                      size={field["lf-options"].length >= 5 ? "sm" : "lg"}
                      bsSize="lg"
                    />
                  );
                } else if (field.type === "checkbox buttons") {
                  fieldComponent = (
                    <CheckboxButtonGroupInput
                      key={key}
                      id={key}
                      name={field.name}
                      label={label}
                      options={field["lf-options"]}
                      optionLabelKey="lf-label"
                      optionValueKey="value"
                      optionIconKey="lf-icon"
                      color="secondary"
                      outline
                      button={field.tag === "button"}
                      btnClassName={
                        field.tag === "button" ? "with-icon" : undefined
                      }
                      size={field["lf-options"].length >= 5 ? "sm" : "lg"}
                      bsSize="lg"
                    />
                  );
                } else if (field.type === "address") {
                  const placeholder = substitutions(
                    field["lf-input-placeholder"] || "",
                    values
                  );
                  fieldComponent = (
                    <AddressInput
                      key={key}
                      id={key}
                      name={field.name}
                      label={label}
                      placeholder={placeholder}
                      autoComplete={field["autocomplete"]}
                      bsSize="lg"
                      floating={floatingLabels}
                    />
                  );
                } else if (field.type === "jornaya") {
                  fieldComponent = (
                    <JornayaInput
                      key={key}
                      id={key}
                      name={field.name}
                      jornayaToken={appState.jornaya}
                    />
                  );
                } else if (field.tag === "input") {
                  if (field.type === "hidden") {
                    fieldComponent = (
                      <FastField key={key} name={field.name}>
                        {({ field: fieldProps }) => (
                          <>
                            {label && (
                              <label htmlFor={key} className="visually-hidden">
                                {label}
                              </label>
                            )}
                            <input id={key} type="hidden" {...fieldProps} />
                          </>
                        )}
                      </FastField>
                    );
                  } else {
                    const placeholder = substitutions(
                      field["lf-input-placeholder"] || "",
                      values
                    );
                    fieldComponent = field["mask"] ? (
                      <FormInputMask
                        key={key}
                        id={key}
                        name={field.name}
                        type={field.type}
                        label={label}
                        placeholder={placeholder}
                        autoComplete={field["autocomplete"]}
                        mask={field["mask"]}
                        bsSize="lg"
                        floating={floatingLabels}
                      />
                    ) : (
                      <FormInput
                        key={key}
                        id={key}
                        name={field.name}
                        type={field.type}
                        label={label}
                        placeholder={placeholder}
                        autoComplete={field["autocomplete"]}
                        bsSize="lg"
                        floating={floatingLabels}
                      />
                    );
                  }
                }

                if (
                  isEmpty(field["lf-condition-field"]) ||
                  (!isEmpty(field["lf-condition-field"]) &&
                    !isEmpty(values[field["lf-condition-field"]]) &&
                    values[field["lf-condition-field"]] ===
                      field["lf-condition-value"])
                ) {
                  fieldComponents.push(fieldComponent);
                }
              });

              return (
                <Form id={FORM_ID} onFocus={handleFormOnFocus}>
                  <FormGroup
                    row
                    cssModule={{
                      "mb-3": !hasNextSection && tcpa ? "mb-0" : "mb-3",
                    }}
                    className={classNames({
                      "mslf-form-content-row": true,
                      "visually-hidden": showSearchSection,
                    })}
                  >
                    {!isEmpty(section.fields) && (
                      <Col xs={12}>
                        {!isEmpty(question) && (
                          <div className="mb-4">
                            <label
                              className={classNames({
                                h3: true,
                                "mb-1": !isEmpty(hint),
                              })}
                              htmlFor={`${FORM_ID}_${section.fields[0].name}`}
                            >
                              {question}
                            </label>
                            {!isEmpty(hint) && (
                              <p className="h6 fw-normal text-black-50">
                                {hint}
                              </p>
                            )}
                          </div>
                        )}
                        {fieldComponents}
                      </Col>
                    )}
                    {!isEmpty(section.images) && (
                      <Col xs={12} className="mslf-form-images">
                        {section.images.map(({ alt, url }, i) => (
                          <div key={`image${i}`}>
                            <img src={url} alt={alt} />
                          </div>
                        ))}
                      </Col>
                    )}
                  </FormGroup>

                  {!hasNextSection && tcpa && (
                    <FormGroup row>
                      <Col xs={12}>
                        <div
                          className="mslf-form-tcpa-language"
                          onClick={tcpaOnClick}
                        >
                          {tcpa}
                        </div>
                      </Col>
                    </FormGroup>
                  )}

                  {!showSearchSection && (
                    <FormGroup
                      row
                      className={classNames({
                        "justify-content-center":
                          !hasPrevSection || !showBackButton,
                      })}
                    >
                      {hasPrevSection && showBackButton && (
                        <Col xs={6}>
                          <Button
                            block
                            color="secondary"
                            onClick={onBack}
                            type="button"
                            size="lg"
                            disabled={
                              isValidating ||
                              isSubmitting ||
                              formState.isSubmitted ||
                              formState.isDisabled
                            }
                            outline
                          >
                            Back
                          </Button>
                        </Col>
                      )}
                      <Col xs={submitButtonWidth}>
                        <Button
                          block
                          color={!primaryButtonStyles ? "primary" : undefined}
                          type="submit"
                          size="lg"
                          disabled={
                            isValidating ||
                            isSubmitting ||
                            formState.isSubmitted ||
                            formState.isDisabled
                          }
                          style={primaryButtonStyles}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm">{submitButtonLabel}</Spinner>
                              <span> {submitButtonLabel}</span>
                            </>
                          ) : hasNextSection ? (
                            nextButtonLabel
                          ) : (
                            submitButtonLabel
                          )}
                        </Button>
                      </Col>
                    </FormGroup>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
}

export default App;
